import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicHeader from './PublicHeader';
import Footer from './Footer';
import { MotionConfig } from 'framer-motion';
import AboutPromo from './about/AboutPromo';
import { LANGUAGES } from '../i18n/languages';
import './PublicMatches.css';
import './about/about.css';
import usePageTitle from '../utils/usePageTitle';

const DISCORD_URL = 'https://discord.gg/PdghFH7TU8';
const CRASH_STEP = 4; // the night's crash, marked in rose

// One evening of a BO3, told in order. The times are just there to make it read like a real night.
const buildNight = (t) => [
  ['19:30', t('aboutPage.night.create.title', 'Set up the match'),
    t('aboutPage.night.create.text', 'Pick the two teams, a server and BO3. If you made a season for the event, the configs, veto style and TeamSpeak details are already filled in.')],
  ['19:40', t('aboutPage.night.veto.title', 'Veto'),
    t('aboutPage.night.veto.text', 'Each captain gets their own link and bans or picks in the browser. Anyone with the public link can watch it happen. When the last map is picked, the server is ready.')],
  ['19:55', t('aboutPage.night.warmup.title', 'Warmup'),
    t('aboutPage.night.warmup.text', 'Players join and type !ready. Anyone who isn’t on the roster gets kicked, and everyone’s name and team avatar are set for them.')],
  ['20:02', t('aboutPage.night.knife.title', 'Knife round'),
    t('aboutPage.night.knife.text', 'The winner types !stay or !swap. If the knife round runs out of time, the side with more total HP takes it (then more players alive, then a coin toss). No arguing in Discord.')],
  ['20:31', t('aboutPage.night.crash.title', 'Someone’s game crashes at 9–7'),
    t('aboutPage.night.crash.text', 'They type !tech and the match pauses. It only resumes once both teams type !unpause. If a round needs replaying, you restore it from the panel. If the server itself gives up, you move the match to another one and carry on from the same score.')],
  ['20:48', t('aboutPage.night.half.title', 'Halftime'),
    t('aboutPage.night.half.text', 'An optional break. Coaches get unmuted on TeamSpeak to talk to their team, and muted again before the second half starts.')],
  ['22:10', t('aboutPage.night.done.title', 'GG'),
    t('aboutPage.night.done.text', 'The score, every player’s stats and the demo link are on the public match page. The bracket moves on by itself and your Discord channel gets the result.')],
];

const buildLists = (t) => [
  {
    title: t('aboutPage.lists.match.title', 'During the match'),
    items: [
      t('aboutPage.lists.match.score', 'Live scoreboard, round history and killfeed'),
      t('aboutPage.lists.match.stats', 'K/D, ADR, KAST, HLTV 2.0 Rating and Flux 3.0 (HLTV 3.0-style, with Round Swing) for every player'),
      t('aboutPage.lists.match.pauses', 'Tech, admin and production pauses'),
      t('aboutPage.lists.match.timeouts', 'Tactical timeouts, also from a coach’s tablet'),
      t('aboutPage.lists.match.restore', 'Automatic round backups and one-click restore'),
      t('aboutPage.lists.match.migrate', 'Move a live match to another server'),
      t('aboutPage.lists.match.console', 'A server console right in the browser'),
      t('aboutPage.lists.match.chat', 'In-game chat for every map'),
      t('aboutPage.lists.match.duration', 'How long every map took, live to finish'),
      t('aboutPage.lists.match.equipment', 'Every player’s money, weapons and utility, live'),
      t('aboutPage.lists.match.highlights', 'Highlights: aces, clutches, Zeus and knife kills, map point'),
    ],
  },
  {
    title: t('aboutPage.lists.event.title', 'Around the event'),
    items: [
      t('aboutPage.lists.event.veto', 'Map veto, with an optional coin flip'),
      t('aboutPage.lists.event.brackets', 'Single and double elimination brackets'),
      t('aboutPage.lists.event.bracketSingle', 'Knockout brackets for any number of teams: byes for top seeds, 3rd place match, best-of per round, results straight from the match'),
      t('aboutPage.lists.event.groups', 'GSL group stages'),
      t('aboutPage.lists.event.stages', 'Swiss stages (fixed rounds or Major style, paired automatically) and round-robin groups, with the playoffs in the same bracket - filled from the standings when the stage ends'),
      t('aboutPage.lists.event.seasons', 'Seasons that remember your settings'),
      t('aboutPage.lists.event.seasonStats', 'Season stats: CT / T, pistol rounds and win reasons per team'),
      t('aboutPage.lists.event.seating', 'Stage seating for LAN events, per team and per match'),
      t('aboutPage.lists.event.teams', 'Team pages with FACEIT levels'),
      t('aboutPage.lists.event.vrs', 'Valve’s VRS rankings'),
    ],
  },
  {
    title: t('aboutPage.lists.servers.title', 'Your servers'),
    items: [
      t('aboutPage.lists.servers.status', 'Status and player count at a glance'),
      t('aboutPage.lists.servers.maps', 'Your own map pools'),
      t('aboutPage.lists.servers.cfg', 'Warmup, knife and live configs'),
      t('aboutPage.lists.servers.files', 'A file manager, so you can skip the FileZilla step'),
      t('aboutPage.lists.servers.linuxgsm', 'LinuxGSM start, stop and restart'),
      t('aboutPage.lists.servers.logs', 'Live match logs you can share'),
      t('aboutPage.lists.servers.steamStatus', 'Steam status: see when CS2 or Steam sign-in is down'),
    ],
  },
  {
    title: t('aboutPage.lists.share.title', 'Sharing and add-ons'),
    items: [
      t('aboutPage.lists.share.public', 'Public match, bracket and team pages'),
      t('aboutPage.lists.share.demos', 'Demo uploads to Gofile, Google Drive, storage.to or Pixeldrain'),
      t('aboutPage.lists.share.discord', 'Discord posts for the events you choose'),
      t('aboutPage.lists.share.sheets', 'Stats export to Google Sheets'),
      t('aboutPage.lists.share.akros', 'Akros anti-cheat'),
      t('aboutPage.lists.share.delay', 'Stream delay, so public pages don’t spoil the broadcast'),
    ],
  },
  {
    title: t('aboutPage.lists.dev.title', 'If you write code'),
    items: [
      t('aboutPage.lists.dev.api', 'A REST API with keys you manage'),
      t('aboutPage.lists.dev.feed', 'A live feed of kills, rounds and scores'),
      t('aboutPage.lists.dev.webhooks', 'Signed webhooks for any match event'),
      t('aboutPage.lists.dev.docs', 'Docs, plus a tester to try every endpoint'),
      t('aboutPage.lists.dev.ext', 'Stats for matches you run on another platform'),
      t('aboutPage.lists.dev.extSeating', 'Stage seating and real player names for the display boards'),
    ],
  },
];

const buildCommands = (t) => [
  ['!ready', t('about.commands.ready', 'Player ready during warmup')],
  ['!stay / !swap', t('about.commands.stay', 'Side choice after knife')],
  ['!tac', t('about.commands.tac', 'Tactical timeout')],
  ['!tech', t('about.commands.tech', 'Tech pause')],
  ['!unpause', t('about.commands.unpause', 'Both teams request resume')],
  ['!restore N', t('about.commands.restore', 'Admin restores round N')],
  ['!fp / !fup', t('about.commands.fp', 'Admin force pause or unpause')],
  ['!pfp', t('aboutPage.commands.pfp', 'Admin production pause')],
  ['!asay', t('about.commands.asay', 'Admin broadcast message')],
];

// The English of every entry except "lang" and "free" is also in server/services/seoContent.js (FAQ markup for
// search engines must match the page) - change both; scripts/seo-test.js compares them.
const buildFaq = (t, count) => [
  [t('aboutPage.faq.what.q', 'What is Flux?'),
    t('aboutPage.faq.what.a', 'Flux (also called FluxCS or FluxBot) is a web panel for running Counter-Strike 2 matches and tournaments. The veto, ready-up, knife round, pauses, round restores, live scores, player stats, brackets and seasons all happen on one page.')],
  [t('aboutPage.faq.compare.q', 'How is Flux different from MatchZy or Get5?'),
    t('aboutPage.faq.compare.a', 'MatchZy and Get5 are plugins you install on the game server. Flux isn’t a plugin: it controls a normal CS2 server over RCON and reads its logs, and adds the web panel, public match pages, brackets and stats on top.')],
  [t('aboutPage.faq.need.q', 'What do I need to run matches with Flux?'),
    t('aboutPage.faq.need.a', 'A CS2 dedicated server with RCON turned on. You add it in the panel with its RCON password, and Flux sets up the log address and everything else itself.')],
  [t('aboutPage.faq.plugin.q', 'Do I have to install a plugin on my CS2 server?'),
    t('aboutPage.faq.plugin.a', 'No. You add the server in the panel and that’s it. Your server stays a normal CS2 server.')],
  [t('aboutPage.faq.free.q', 'Is it free?'),
    t('aboutPage.faq.free.a', 'Right now, yes. Flux is in beta, and we’d rather have people running real matches on it than charge for it.')],
  [t('aboutPage.faq.lan.q', 'Does it work at a LAN?'),
    t('aboutPage.faq.lan.a', 'Yes, see the LAN section above. You don’t need to change anything on the venue network.')],
  [t('aboutPage.faq.lang.q', 'Can my admins use it in their own language?'),
    t('aboutPage.faq.lang.a', 'There are {{count}} languages so far. Pick yours from the flag in the top corner.', { count })],
  [t('aboutPage.faq.access.q', 'How do I get access?'),
    t('aboutPage.faq.access.a', 'Come say hi on our Discord and we’ll get you set up.')],
];

function Section({ children, shade }) {
  return (
    <section className={`ab-sec${shade ? ' shade' : ''}`}>
      <div className="ab-wrap">{children}</div>
    </section>
  );
}

function About() {
  const { t } = useTranslation();
  usePageTitle(t('seo.aboutFullTitle', 'About Flux - CS2 Tournament Panel, No Server Plugin Needed'), true);
  const location = useLocation();
  const night = buildNight(t);
  const lists = buildLists(t);
  const commands = buildCommands(t);
  const faq = buildFaq(t, LANGUAGES.length);

  // /about?promo=capture renders only the promo, full screen (used to record it as a video).
  if (new URLSearchParams(location.search).get('promo') === 'capture') {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100vw', maxWidth: '177.78vh' }}><AboutPromo capture /></div>
      </div>
    );
  }

  return (
    <div className="pub-root ab">
      <PublicHeader />

      {/* intro: the text on 5 columns, the promo on 7 */}
      <section className="ab-hero">
        <div className="ab-wrap">
          <div>
            <h1>{t('aboutPage.intro.title', 'We got tired of running matches from five windows.')}</h1>
            <p className="ab-lead">
              {t('aboutPage.intro.p1', 'The server console in one, a spreadsheet in another, Discord pinging, and someone asking what the score is. Running a CS2 match shouldn’t feel like that.')}
            </p>
            <p className="ab-lead">
              {t('aboutPage.intro.p2', 'Flux puts the whole match on one page, so the admin can actually watch the game.')}
            </p>
            <div className="ab-actions">
              <Link to="/matches" className="ab-btn primary">{t('about.viewLiveMatches', 'View live matches')}</Link>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="ab-btn ghost">{t('nav.joinDiscord', 'Join Discord')}</a>
            </div>
          </div>
          <MotionConfig reducedMotion="user"><AboutPromo /></MotionConfig>
        </div>
      </section>

      {/* a match night: the heading stays while the evening scrolls past */}
      <Section shade>
        <div className="ab-night">
          <div className="ab-night-intro">
            <h2>{t('aboutPage.night.title', 'What a match night looks like')}</h2>
            <p className="ab-lead">{t('aboutPage.night.lead', 'A normal BO3, start to finish, including the part where something goes wrong. Something always goes wrong.')}</p>
          </div>
          <ol className="ab-steps">
            {night.map(([time, title, text], i) => (
              <li key={time} className={`ab-step${i === CRASH_STEP ? ' crash' : ''}`}>
                <span className="ab-time">{time}</span>
                <div className="ab-body">
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      {/* everything else */}
      <Section>
        <div className="ab-head">
          <h2>{t('aboutPage.lists.title', 'Everything else it does')}</h2>
          <p className="ab-lead">
            {t('aboutPage.lists.lead', 'The short version. Most of this came from someone at a real event asking “can it also do…?”')}
          </p>
        </div>
        <div className="ab-spec">
          {lists.map((group) => (
            <div key={group.title} className="ab-spec-row">
              <h3>{group.title}</h3>
              <ul>
                {group.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* LAN */}
      <Section shade>
        <div className="ab-split">
          <div>
            <h2>{t('aboutPage.lan.title', 'Running a LAN?')}</h2>
            <p className="ab-lead">{t('aboutPage.lan.p1', 'Venue internet is never great, and nobody wants to touch the venue’s router an hour before the first match.')}</p>
            <p className="ab-lead">{t('aboutPage.lan.p2', 'Run one install command on each machine and approve it in the panel. Its CS2 servers show up ready to add, named Machine 1, Machine 2 and so on, not some random ID. If the internet drops halfway through a map, nothing from the match is lost.')}</p>
          </div>
          <ol className="ab-facts">
            {[
              t('aboutPage.lan.f1', 'One command for every machine'),
              t('aboutPage.lan.f2', 'Servers are found for you'),
              t('aboutPage.lan.f3', 'Pick match control only, or full access with backups and files'),
              t('aboutPage.lan.f4', 'Update or remove it with one command'),
              t('aboutPage.lan.f5', 'Works on Linux and Windows server machines'),
            ].map((f) => <li key={f}>{f}</li>)}
          </ol>
        </div>
      </Section>

      {/* chat commands: the full width, three columns */}
      <Section>
        <div className="ab-head">
          <h2>{t('aboutPage.chat.title', 'Players don’t need the panel')}</h2>
          <p className="ab-lead">{t('aboutPage.chat.text', 'Everything a player needs works from in-game chat. Admins get a few extra commands for when they’re on the server themselves.')}</p>
        </div>
        <ul className="ab-cmds">
          {commands.map(([cmd, desc]) => (
            <li key={cmd}><code>{cmd}</code><span>{desc}</span></li>
          ))}
        </ul>
      </Section>

      {/* FAQ */}
      <Section shade>
        <div className="ab-head">
          <h2>{t('aboutPage.faq.title', 'Questions people ask us')}</h2>
        </div>
        <dl className="ab-faq">
          {faq.map(([q, a]) => (
            <div key={q}>
              <dt>{q}</dt>
              <dd>{a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* note from the team, then the ways in */}
      <Section>
        <figure className="ab-note">
          <blockquote>
            “{t('aboutPage.note.text', 'Pretty much every button on the admin page is there because something went wrong at a real match. If something you need is missing, tell us. That’s how most of Flux got built.')}”
          </blockquote>
          <figcaption>{t('aboutPage.note.sign', '— The Flux team at LOTGaming')}</figcaption>
          <div className="ab-actions">
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="ab-btn primary">{t('nav.joinDiscord', 'Join Discord')}</a>
            <Link to="/matches" className="ab-btn ghost">{t('nav.matches', 'View matches')}</Link>
            <Link to="/teams" className="ab-btn ghost">{t('about.browseTeams', 'Browse teams')}</Link>
          </div>
        </figure>
      </Section>

      <Footer />
    </div>
  );
}

export default About;
